import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from "react";
import { useAuth } from "./AuthContext";
import { supabase } from "@/integrations/supabase/client";

// ========================
// TYPES
// ========================

export type CompanyStatus = "Active" | "Inactive" | "Trial";

export interface CompanyMember {
  userId: string;
  userName: string;
  email: string;
  role: "owner" | "admin" | "ca" | "accountant" | "viewer";
  joinedAt: string;
}

export interface Company {
  id: string;
  name: string;
  tradeName?: string;
  legalName: string;
  businessConstitution?: string;
  industry?: string;
  industryLabel?: string;
  ownerName?: string;
  gstin: string;
  pan: string;
  cin?: string;
  address: string;
  city: string;
  district?: string;
  state: string;
  stateCode?: string;
  pincode: string;
  phone: string;
  email: string;
  website?: string;
  financialYearStart: string; // "04" for April
  periodLockDate?: string; // YYYY-MM-DD
  booksBeginningFrom?: string; // YYYY-MM-DD
  baseCurrency: string;
  gstRegistered: boolean;
  compositionScheme: boolean;
  status: CompanyStatus;
  onboardingCompleted: boolean;
  onboardingStep?: number;
  plan: string;
  members: CompanyMember[];
  createdAt: string;
  updatedAt: string;
}

interface CompanyContextType {
  companies: Company[];
  activeCompany: Company | null;
  activeCompanyId: string | null;
  switchCompany: (id: string) => void;
  refreshCompanies: () => Promise<void>;
  addCompany: (data: Omit<Company, "id" | "createdAt" | "updatedAt" | "members">) => Promise<Company>;
  updateCompany: (id: string, data: Partial<Company>) => Promise<void>;
  deleteCompany: (id: string) => Promise<void>;
  addMember: (companyId: string, member: Omit<CompanyMember, "joinedAt">) => void;
  removeMember: (companyId: string, userId: string) => void;
  getUserCompanies: () => Company[];
  isAuthorized: (companyId: string) => boolean;
  loading: boolean;
}

const CompanyContext = createContext<CompanyContextType | null>(null);

function mapDbCompany(row: any): Company {
  let members: CompanyMember[] = [];
  if (row.members) {
    try {
      members = typeof row.members === "string" ? JSON.parse(row.members) : row.members;
    } catch {
      members = [];
    }
  }

  return {
    id: row.id,
    name: row.name,
    tradeName: row.trade_name || row.tradeName || undefined,
    legalName: row.legal_name || row.legalName || row.name,
    businessConstitution: row.business_constitution || row.businessConstitution || undefined,
    industry: row.industry || undefined,
    industryLabel: row.industry_label || row.industryLabel || undefined,
    ownerName: row.owner_name || row.ownerName || undefined,
    gstin: row.gstin || "",
    pan: row.pan || "",
    cin: row.cin || undefined,
    address: row.address || "",
    city: row.city || "",
    district: row.district || undefined,
    state: row.state || "",
    stateCode: row.state_code || row.stateCode || undefined,
    pincode: row.pincode || "",
    phone: row.phone || "",
    email: row.email || "",
    website: row.website || undefined,
    financialYearStart: row.financial_year_start || row.financialYearStart || "04",
    baseCurrency: row.base_currency || row.baseCurrency || "INR",
    gstRegistered: row.gst_registered !== undefined ? Boolean(row.gst_registered) : true,
    compositionScheme: Boolean(row.composition_scheme),
    status: (row.status as CompanyStatus) || "Active",
    onboardingCompleted: Boolean(row.onboarding_completed),
    onboardingStep: row.onboarding_step || 1,
    plan: row.plan || "starter",
    members,
    createdAt: row.created_at || new Date().toISOString(),
    updatedAt: row.updated_at || new Date().toISOString(),
  };
}

export function CompanyProvider({ children }: { children: ReactNode }) {
  const { user, role } = useAuth();
  const [companies, setCompanies] = useState<Company[]>([]);
  const [activeCompanyId, setActiveCompanyId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Load companies from MySQL
  const loadCompanies = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: compData, error: compError }, { data: memberData }] = await Promise.all([
        supabase.from("companies").select("*"),
        supabase.from("company_members").select("*")
      ]);

      if (!compError && Array.isArray(compData)) {
        const membersList = Array.isArray(memberData) ? memberData : [];
        const mapped = compData.map((row: any) => {
          const company = mapDbCompany(row);
          const compMembers = membersList
            .filter((m: any) => m.company_id === company.id)
            .map((m: any) => ({
              userId: m.user_id,
              userName: m.user_name || "Team Member",
              email: m.email || "",
              role: m.role || "accountant",
              joinedAt: m.joined_at ? new Date(m.joined_at).toISOString().split("T")[0] : "",
            }));
          company.members = compMembers;
          return company;
        });
        setCompanies(mapped);

        // Select active company scoped to user's authorized companies
        const userComps = role === "super_admin"
          ? mapped
          : mapped.filter((c) => c.id === user?.company || (c.members && c.members.some((m) => m.userId === user?.id)));

        if (userComps.length > 0) {
          setActiveCompanyId((current) => {
            if (current && userComps.some((c) => c.id === current)) return current;
            if (user?.company && userComps.some((c) => c.id === user.company)) return user.company;
            return userComps[0].id;
          });
        } else {
          setActiveCompanyId(null);
        }
      }
    } catch (err) {
      console.error("[CompanyContext] Failed to load companies from MySQL", err);
    } finally {
      setLoading(false);
    }
  }, [user?.id, user?.company, role]);

  useEffect(() => {
    loadCompanies();
  }, [loadCompanies]);

  useEffect(() => {
    if (activeCompanyId) {
      localStorage.setItem("accoupro_active_company_id", activeCompanyId);
    } else {
      localStorage.removeItem("accoupro_active_company_id");
    }
  }, [activeCompanyId]);

  const getUserCompanies = useCallback(() => {
    if (role === "super_admin") return companies;
    if (!user) return [];
    return companies.filter(
      (c) => c.id === user.company || (c.members && c.members.some((m) => m.userId === user.id))
    );
  }, [companies, user, role]);

  const isAuthorized = useCallback((companyId: string) => {
    if (role === "super_admin") return true;
    if (!user) return false;
    const userComps = getUserCompanies();
    return userComps.some((c) => c.id === companyId);
  }, [role, user, getUserCompanies]);

  const userCompsList = getUserCompanies();
  const activeCompany = activeCompanyId
    ? userCompsList.find((c) => c.id === activeCompanyId) || userCompsList[0] || null
    : userCompsList[0] || null;

  const switchCompany = useCallback((id: string) => {
    if (isAuthorized(id)) {
      setActiveCompanyId(id);
    }
  }, [isAuthorized]);

  const addCompany = useCallback(async (data: Omit<Company, "id" | "createdAt" | "updatedAt" | "members">) => {
    const now = new Date().toISOString();
    const id = `comp-${Date.now().toString(36)}`;
    const members: CompanyMember[] = user
      ? [{ userId: user.id, userName: user.name || "Admin", email: user.email, role: "owner", joinedAt: now }]
      : [];

    const dbPayload = {
      id,
      name: data.name,
      legal_name: data.legalName || data.name,
      gstin: data.gstin || null,
      pan: data.pan || null,
      cin: data.cin || null,
      address: data.address || null,
      city: data.city || null,
      state: data.state || null,
      pincode: data.pincode || null,
      phone: data.phone || null,
      email: data.email || null,
      website: data.website || null,
      financial_year_start: data.financialYearStart || "04",
      base_currency: data.baseCurrency || "INR",
      gst_registered: data.gstRegistered,
      composition_scheme: data.compositionScheme,
      status: data.status || "Active",
      plan: data.plan || "starter",
      created_by: user?.id || null,
    };

    const { data: inserted, error } = await supabase.from("companies").insert(dbPayload).select().single();
    if (error) {
      console.error("[CompanyContext] addCompany error:", error);
    }

    const company: Company = {
      ...data,
      id: inserted?.id || id,
      members,
      createdAt: now,
      updatedAt: now,
    };

    setCompanies((prev) => [company, ...prev]);
    setActiveCompanyId(company.id);

    // Update user's company profile and insert company_members
    if (user?.id) {
      await supabase.from("profiles").update({ company_id: company.id }).eq("id", user.id);
      await supabase.from("users").update({ company_id: company.id }).eq("id", user.id);
      await supabase.from("company_members").insert({
        company_id: company.id,
        user_id: user.id,
        user_name: user.name || "Admin",
        email: user.email,
        role: "owner",
      });
    }

    // Auto-create standard zero-balance system ledgers for the new company
    const standardLedgers = [
      { id: `ldg-cash-${company.id}`, name: 'Cash-in-Hand', group: 'Cash-in-Hand', nature: 'Asset' },
      { id: `ldg-bank-${company.id}`, name: 'Bank Account', group: 'Bank Accounts', nature: 'Asset' },
      { id: `ldg-stock-${company.id}`, name: 'Stock-in-Hand / Inventory Asset', group: 'Stock-in-Hand', nature: 'Asset' },
      { id: `ldg-cogs-${company.id}`, name: 'Cost of Goods Sold (COGS)', group: 'Direct Expenses', nature: 'Expense' },
      { id: `ldg-sales-${company.id}`, name: 'Sales Account', group: 'Sales Accounts', nature: 'Income' },
      { id: `ldg-purchase-${company.id}`, name: 'Purchase Account', group: 'Purchase Accounts', nature: 'Expense' },
      { id: `ldg-capital-${company.id}`, name: 'Capital Account', group: 'Capital Account', nature: 'Equity' },
      { id: `ldg-cgst-out-${company.id}`, name: 'CGST Output (9%)', group: 'Duties & Taxes', nature: 'Liability' },
      { id: `ldg-sgst-out-${company.id}`, name: 'SGST Output (9%)', group: 'Duties & Taxes', nature: 'Liability' },
      { id: `ldg-igst-out-${company.id}`, name: 'IGST Output (18%)', group: 'Duties & Taxes', nature: 'Liability' },
      { id: `ldg-cgst-in-${company.id}`, name: 'CGST Input (9%)', group: 'Duties & Taxes', nature: 'Asset' },
      { id: `ldg-sgst-in-${company.id}`, name: 'SGST Input (9%)', group: 'Duties & Taxes', nature: 'Asset' },
      { id: `ldg-igst-in-${company.id}`, name: 'IGST Input (18%)', group: 'Duties & Taxes', nature: 'Asset' },
      { id: `ldg-roundoff-${company.id}`, name: 'Round Off', group: 'Indirect Expenses', nature: 'Expense' }
    ];

    for (const l of standardLedgers) {
      await supabase.from("ledgers").insert({
        id: l.id,
        company_id: company.id,
        name: l.name,
        group: l.group,
        nature: l.nature,
        opening_balance: 0,
        opening_balance_type: 'Dr',
        current_balance: 0,
        current_balance_type: 'Dr',
        is_system: true,
        created_by: user?.id || ""
      });
    }

    return company;
  }, [user]);

  const updateCompany = useCallback(async (id: string, data: Partial<Company>) => {
    setCompanies((prev) => prev.map((c) => (c.id === id ? { ...c, ...data, updatedAt: new Date().toISOString() } : c)));

    const dbUpdate: Record<string, any> = {};
    if (data.name !== undefined) dbUpdate.name = data.name;
    if (data.legalName !== undefined) dbUpdate.legal_name = data.legalName;
    if (data.gstin !== undefined) dbUpdate.gstin = data.gstin;
    if (data.pan !== undefined) dbUpdate.pan = data.pan;
    if (data.phone !== undefined) dbUpdate.phone = data.phone;
    if (data.email !== undefined) dbUpdate.email = data.email;
    if (data.address !== undefined) dbUpdate.address = data.address;
    if (data.city !== undefined) dbUpdate.city = data.city;
    if (data.state !== undefined) dbUpdate.state = data.state;
    if (data.pincode !== undefined) dbUpdate.pincode = data.pincode;

    await supabase.from("companies").update(dbUpdate).eq("id", id);
  }, []);

  const deleteCompany = useCallback(async (id: string) => {
    setCompanies((prev) => prev.filter((c) => c.id !== id));
    if (activeCompanyId === id) {
      const remaining = companies.filter((c) => c.id !== id);
      setActiveCompanyId(remaining.length > 0 ? remaining[0].id : null);
    }
    await supabase.from("company_members").delete().eq("company_id", id);
    await supabase.from("companies").delete().eq("id", id);
  }, [activeCompanyId, companies]);

  const addMember = useCallback(async (companyId: string, member: Omit<CompanyMember, "joinedAt">) => {
    const joinedAt = new Date().toISOString();
    setCompanies((prev) => prev.map((c) => {
      if (c.id !== companyId) return c;
      if (c.members.some((m) => m.userId === member.userId)) return c;
      return { ...c, members: [...c.members, { ...member, joinedAt }], updatedAt: joinedAt };
    }));

    try {
      await supabase.from("company_members").insert({
        company_id: companyId,
        user_id: member.userId,
        user_name: member.userName,
        email: member.email,
        role: member.role,
      });
    } catch (e) {
      console.error("[CompanyContext] Failed to save company member:", e);
    }
  }, []);

  const removeMember = useCallback(async (companyId: string, userId: string) => {
    setCompanies((prev) => prev.map((c) => {
      if (c.id !== companyId) return c;
      return { ...c, members: c.members.filter((m) => m.userId !== userId), updatedAt: new Date().toISOString() };
    }));

    try {
      await supabase.from("company_members").delete().eq("company_id", companyId).eq("user_id", userId);
    } catch (e) {
      console.error("[CompanyContext] Failed to remove company member:", e);
    }
  }, []);

  return (
    <CompanyContext.Provider value={{
      companies, activeCompany, activeCompanyId: activeCompany?.id || activeCompanyId || null, switchCompany, refreshCompanies: loadCompanies, addCompany, updateCompany,
      deleteCompany, addMember, removeMember, getUserCompanies, isAuthorized, loading,
    }}>
      {children}
    </CompanyContext.Provider>
  );
}

export function useCompany() {
  const ctx = useContext(CompanyContext);
  if (!ctx) throw new Error("useCompany must be used within CompanyProvider");
  return ctx;
}
